import React, { ReactNode } from "react";
import styled from "styled-components";
import { LanguageNameSpaces, TimeRange } from "@shared/types";
import { BASE_CHART_TIME_RANGES } from "@shared/common/constants";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import Panel from "common/NewLook/Panel.tsx";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import SegmentTabs from "common/NewLook/SegmentTabs.tsx";

interface NewLookChartCardProps {
  title: ReactNode;
  chart: ReactNode;
  timeRange?: TimeRange;
  setTimeRange?: (timeRange: TimeRange) => void;
  /** Height of the chart area; the panel itself sizes to its content. */
  chartHeight?: number;
  className?: string;
  /** Title row and chart only: the overview panel supplies the surface. */
  flush?: boolean;
}

/**
 * New-look chart panel: the title and the time-range track in the panel
 * header (the dashboard's pattern), the chart filling the body.
 */
const NewLookChartCard = ({
  title,
  chart,
  timeRange,
  setTimeRange,
  chartHeight = NEW_LOOK_CHART_HEIGHT,
  className,
  flush,
}: NewLookChartCardProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const tabs = timeRange && setTimeRange && (
    <SegmentTabs
      options={BASE_CHART_TIME_RANGES}
      value={timeRange}
      onChange={setTimeRange}
      ariaLabel={t("charts.range_label")}
    />
  );
  if (flush)
    return (
      <FlushRoot className={className}>
        <FlushHeader>
          <FlushTitle>{title}</FlushTitle>
          {tabs}
        </FlushHeader>
        <FlushChart style={{ minHeight: chartHeight }}>
          <ChartFill>{chart}</ChartFill>
        </FlushChart>
      </FlushRoot>
    );
  return (
    <ChartPanel className={className} title={title} actions={tabs}>
      <ChartArea style={{ minHeight: chartHeight }}>
        <ChartFill>{chart}</ChartFill>
      </ChartArea>
    </ChartPanel>
  );
};

export default NewLookChartCard;

export const NEW_LOOK_CHART_HEIGHT = 176;

const ChartPanel = styled(Panel)`
  height: 100%;
  min-height: 0;
  overflow: hidden;
  > div:last-child {
    display: flex;
    flex-direction: column;
    padding: 12px 24px 16px;
  }
`;
const ChartArea = styled.div`
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
`;
const ChartFill = styled.div`
  position: absolute;
  inset: 0;
`;
const FlushRoot = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;
const FlushHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 16px;
  min-height: 30px;
  > :last-child {
    flex: none;
  }
`;
const FlushTitle = styled.h3`
  display: block;
  flex: 1 0 auto;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  align-items: center;
  margin: 0;
  ${newLookType.panelTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const FlushChart = styled(ChartArea)`
  margin-top: 12px;
`;
